var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as Yup from 'yup';
import { Formik } from 'formik';
import { Box, Button, FormHelperText, TextField, Typography } from '@mui/material';
import useAuth from '../../../hooks/useAuth';
import useMounted from '../../../hooks/useMounted';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTheme } from '@mui/material/styles';
import { track } from '../../../analytics/analytics';
import { eventTypes } from '../../../analytics/constants';
const ForgotPassword = props => {
    const theme = useTheme();
    const mounted = useMounted();
    const [recoveryCodeSent, setRecoveryCodeSent] = useState(false);
    const { authenticationManager, changeLoggedInUser } = useAuth();
    const [passwordShown, setPasswordShown] = useState(false);
    const handleSubmit = (values_1, _a) => __awaiter(void 0, [values_1, _a], void 0, function* (values, { setErrors, setStatus, setSubmitting }) {
        const email = values.email.toLowerCase();
        try {
            if (!authenticationManager.accountManager) {
                throw new Error('Forgot password operation not supported');
            }
            if (!recoveryCodeSent) {
                yield authenticationManager.accountManager.forgotPassword(email);
                track(eventTypes.PASSWORD_RESET_REQUESTED, { email });
                setRecoveryCodeSent(true);
            }
            else {
                const { code, password } = values;
                const data = yield authenticationManager.accountManager.forgotPasswordSubmit(email, code, password);
                if (data === 'SUCCESS') {
                    track(eventTypes.PASSWORD_RESET_COMPLETED, { email });
                    const user = yield authenticationManager.signIn(email, values.password);
                    changeLoggedInUser(user);
                }
            }
            if (mounted.current) {
                setStatus({ success: true });
                setSubmitting(false);
            }
        }
        catch (err) {
            toast.error(err.message);
            if (mounted.current) {
                setStatus({ success: false });
                setErrors({ submit: err.message });
                setSubmitting(false);
            }
        }
    });
    const email = Yup.string().email('Must be a valid email').max(255).required('Email is required');
    const validationSchemaPreSend = Yup.object().shape({
        email,
    });
    const validationSchemaPostSend = Yup.object().shape({
        email,
        code: Yup.string().max(255).required('Code is required'),
        password: Yup.string().max(255).required('Password is required'),
        passwordRepeat: Yup.string()
            .max(255)
            .required('Password is required')
            .oneOf([Yup.ref('password'), null], 'Passwords must match'),
    });
    const validationSchema = recoveryCodeSent ? validationSchemaPostSend : validationSchemaPreSend;
    return (_jsx("div", Object.assign({}, props, { children: _jsx(Formik, { initialValues: {
                email: '',
                code: '',
                password: '',
                passwordRepeat: '',
                submit: '',
            }, validationSchema: validationSchema, onSubmit: handleSubmit, children: formik => {
                const { errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values } = formik;
                return (_jsxs("form", Object.assign({ noValidate: true, onSubmit: handleSubmit }, props, { children: [_jsxs("div", { className: "form-inputs", children: [_jsx("div", { className: "form-input", children: _jsxs("label", { children: ["Email address", _jsx(TextField, { error: Boolean(touched.email && errors.email), fullWidth: true, helperText: touched.email && errors.email, margin: "dense", name: "email", onBlur: handleBlur, onChange: handleChange, type: "email", value: values.email, variant: "outlined", size: "small" })] }) }), recoveryCodeSent && (_jsxs(_Fragment, { children: [_jsx(Typography, { style: { color: theme.palette.global.lightGray, marginBottom: '.5rem' }, children: "A code has been sent to your email." }), _jsx("div", { className: "form-input", style: { marginTop: '1rem' }, children: _jsxs("label", { children: ["Code", _jsx(TextField, { error: Boolean(touched.code && errors.code), fullWidth: true, helperText: touched.code && errors.code, margin: "dense", name: "code", onBlur: handleBlur, onChange: handleChange, type: "code", value: values.code, variant: "outlined", size: "small" })] }) }), _jsx("div", { className: "form-input", style: { marginTop: '1rem' }, children: _jsxs("label", { children: ["New Password", _jsx(TextField, { error: Boolean(touched.password && errors.password), fullWidth: true, helperText: touched.password && errors.password, margin: "dense", name: "password", onBlur: handleBlur, onChange: handleChange('password'), type: passwordShown ? 'text' : 'password', value: values.password, variant: "outlined", size: "small" })] }) }), _jsx("div", { className: "form-input", style: { marginTop: '1rem' }, children: _jsxs("label", { children: ["Repeat New Password", _jsx(TextField, { error: Boolean(touched.passwordRepeat && errors.passwordRepeat), fullWidth: true, helperText: touched.passwordRepeat && errors.passwordRepeat, 
                                                        // label="Password"
                                                        margin: "dense", name: "passwordRepeat", onBlur: handleBlur, onChange: handleChange('passwordRepeat'), type: passwordShown ? 'text' : 'password', value: values.passwordRepeat, variant: "outlined", size: "small" })] }) })] }))] }), errors.submit && (_jsx(Box, { sx: { mt: 3 }, children: _jsx(FormHelperText, { error: true, children: errors.submit }) })), _jsx(Box, { sx: { mt: 3 }, gap: 3, children: _jsx(Button, { sx: {
                                    backgroundColor: theme.palette.main,
                                    color: 'black',
                                    fontWeight: 'bold',
                                    padding: '.4rem 2.45rem',
                                    '&:hover': {
                                        backgroundColor: theme.typography.button.hover,
                                    },
                                }, disabled: isSubmitting, size: "large", type: "submit", variant: "contained", children: recoveryCodeSent ? 'Reset Password' : 'Send Code' }) }), _jsx(Box, { className: "forgot-password italic", sx: { mt: 2 }, children: _jsx(Link, { to: "/", children: "Sign in" }) })] })));
            } }) })));
};
export default ForgotPassword;
//# sourceMappingURL=ForgotPassword.js.map